import { useEffect, useRef, useState } from 'react';
import { useUiStore } from '../stores/uiStore';
import { useAuthStore } from '../stores/authStore';
import { useThemeStore } from '../stores/themeStore';
import { api } from '../services/api';
import { RunBadge, RunToasts } from './RunIndicator';
import './TopNav.css';

type WorkspaceId = 'game' | 'social' | 'ads';

/**
 * The centre tabs are the workflow, left to right in the order work moves, and
 * follow Odin's four approval gates (Chunk W-Gates, 2026-09-26): Word Bank,
 * Content, Schedule, Social, then Sponsors (hangs off the Schedule, gates
 * nothing).
 *
 * Word Bank and Schedule are PAGES rather than workspaces (no binder of their
 * own), so they are rendered as tabs but switch via setPage. The others switch
 * the workspace, which clears activePage — that is what makes them mutually
 * exclusive without extra bookkeeping.
 */
interface Tab {
  id: string;
  label: string;
  kind: 'page' | 'workspace';
  icon?: string;
  accent: string;
}

const TABS: Tab[] = [
  { id: 'wordbank', label: 'Word Bank', kind: 'page', icon: '/words-am-what-wordbank-icon.png', accent: '#8B6E9B' },
  { id: 'game', label: 'Content', kind: 'workspace', icon: '/words-am-what-gamecontent-icon.png', accent: '#8B6E9B' },
  { id: 'schedule', label: 'Schedule', kind: 'page', icon: '/words-am-what-schedule-icon.png', accent: '#8B6E9B' },
  // Social (2026-10-29) is the Social panel page (lesson posts, phase 1). The old Social workspace
  // screens stay unmounted.
  { id: 'socialpanel', label: 'Social', kind: 'page', icon: '/words-am-what-socialmedia-icon.png', accent: '#8B6E9B' },
  { id: 'ads', label: 'Sponsors', kind: 'workspace', icon: '/words-am-what-ads-icon.png', accent: '#6B8E5E' },
  // Reports (2026-10-21): how the game is doing. Read only; gates nothing, so it sits last.
  { id: 'reports', label: 'Reports', kind: 'page', accent: '#8B6E9B' },
];

/**
 * Tuning tools. Five utilities that were five separate icons competing with the
 * workflow for the same eye-line; they are a menu now. Order is fixed and
 * deliberate. Their pages and keyboard shortcuts are unchanged. Copy Pools
 * (2026-09-28) edits the generic site copy in D1 copy_pools and sits last.
 */
const TOOLS: { id: string; label: string; hint?: string }[] = [
  { id: 'learning', label: 'Learning Layer' },
  { id: 'knowledge', label: 'Knowledge Layer' },
  { id: 'testbench', label: 'Model Test Bench' },
  { id: 'promptbench', label: 'Prompt Test Bench' },
  { id: 'ratings', label: 'Model Ratings' },
  { id: 'copypools', label: 'Copy Pools' },
];

const TOOL_IDS = TOOLS.map((t) => t.id);

export default function TopNav() {
  const { activeWorkspace, activePage, setWorkspace, setPage, toggleSidebar, toggleChat } = useUiStore();
  const logout = useAuthStore((s) => s.logout);
  const { theme, toggleTheme } = useThemeStore();
  const [pendingProposals, setPendingProposals] = useState(0);
  const [costToday, setCostToday] = useState<number | null>(null);
  const [toolsOpen, setToolsOpen] = useState(false);
  const toolsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const data = await api.get('/api/learning/proposals?status=pending') as { pending?: number; total?: number };
        if (cancelled) return;
        setPendingProposals(data?.pending ?? data?.total ?? 0);
      } catch {
        // Silent — count badge is informational; keep last known value.
      }
      try {
        const cost = await api.get('/api/config/cost') as { today?: { usd?: number } };
        if (!cancelled) setCostToday(cost?.today?.usd ?? 0);
      } catch {
        // Silent — cost meter is informational.
      }
    };
    refresh();
    const interval = window.setInterval(refresh, 60_000);
    const onFocus = () => refresh();
    const onUpdated = () => refresh();
    window.addEventListener('focus', onFocus);
    window.addEventListener('learning:proposals-updated', onUpdated);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('learning:proposals-updated', onUpdated);
    };
  }, []);

  // Close the Tools menu on an outside click or Escape, so it cannot be left
  // hanging over the page.
  useEffect(() => {
    if (!toolsOpen) return;
    const onDown = (e: MouseEvent) => {
      if (toolsRef.current && !toolsRef.current.contains(e.target as Node)) setToolsOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setToolsOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [toolsOpen]);

  const tabIsActive = (tab: Tab) =>
    tab.kind === 'page' ? activePage === tab.id : activePage === null && activeWorkspace === tab.id;

  const openTab = (tab: Tab) => {
    if (tab.kind === 'page') setPage(activePage === tab.id ? null : tab.id);
    else setWorkspace(tab.id as WorkspaceId);   // setWorkspace clears activePage
  };

  const toolsActive = TOOL_IDS.includes(activePage ?? '');
  const activeToolLabel = TOOLS.find((t) => t.id === activePage)?.label;

  return (
    <nav className="topnav">
      <div className="topnav-left">
        <button className="topnav-toggle" onClick={toggleSidebar} title="Toggle Binder (Cmd+B)">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M2 4h14M2 9h14M2 14h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </button>
        <div className="topnav-brand">
          <img src="/waw-icon.png" alt="" className="topnav-brand-icon" />
          <span className="topnav-brand-text">Content Engine</span>
        </div>
      </div>

      <div className="topnav-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`topnav-tab ${tabIsActive(tab) ? 'active' : ''}`}
            style={{ '--tab-accent': tab.accent } as React.CSSProperties}
            onClick={() => openTab(tab)}
          >
            {tab.icon ? (
              <img src={tab.icon} alt="" className="topnav-tab-icon" />
            ) : (
              <svg className="topnav-tab-icon" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                <path d="M2.5 5.5h13M2.5 9h13M2.5 12.5h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <circle cx="14" cy="12.5" r="2.2" stroke="currentColor" strokeWidth="1.5"/>
              </svg>
            )}
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="topnav-right">
        <button className="topnav-icon-btn" onClick={toggleChat} title="Toggle Chat (Cmd+J)">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M3 13.5V4.5A1.5 1.5 0 014.5 3h9A1.5 1.5 0 0115 4.5v6a1.5 1.5 0 01-1.5 1.5H6l-3 3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
          </svg>
        </button>

        <div className="topnav-tools" ref={toolsRef}>
          <button
            className={`topnav-icon-btn topnav-tools-btn ${toolsActive ? 'active' : ''}`}
            onClick={() => setToolsOpen((o) => !o)}
            title={
              activeToolLabel
                ? `Tools — ${activeToolLabel} open`
                : pendingProposals > 0
                  ? `Tools — ${pendingProposals} prompt improvement proposal${pendingProposals === 1 ? '' : 's'} pending`
                  : 'Tools'
            }
            aria-haspopup="menu"
            aria-expanded={toolsOpen}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M11.5 2.5a3.5 3.5 0 00-4.2 4.6L2.6 11.8a1.3 1.3 0 101.9 1.9l4.7-4.7a3.5 3.5 0 004.6-4.2L11.9 7 9.3 6.7 9 4.1l2.5-1.6z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
            </svg>
            <span className="topnav-tools-caret" aria-hidden="true">▾</span>
            {/* The Learning Layer's badge lives here now that its icon is inside the menu. */}
            {pendingProposals > 0 && (
              <span className="topnav-badge" aria-label={`${pendingProposals} pending proposals`}>
                {pendingProposals > 99 ? '99+' : pendingProposals}
              </span>
            )}
          </button>

          {toolsOpen && (
            <div className="topnav-menu" role="menu">
              {TOOLS.map((tool) => (
                <button
                  key={tool.id}
                  role="menuitem"
                  className={`topnav-menu-item ${activePage === tool.id ? 'active' : ''}`}
                  onClick={() => {
                    setPage(activePage === tool.id ? null : tool.id);
                    setToolsOpen(false);
                  }}
                >
                  <span>{tool.label}</span>
                  {tool.id === 'learning' && pendingProposals > 0 && (
                    <span className="topnav-menu-badge">{pendingProposals > 99 ? '99+' : pendingProposals}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <button className="topnav-icon-btn" onClick={toggleTheme} title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
          {theme === 'dark' ? (
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M14.5 10.5A6 6 0 017.5 3.5a6 6 0 107 7z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <circle cx="9" cy="9" r="3.5" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M9 1.5v2M9 14.5v2M1.5 9h2M14.5 9h2M3.4 3.4l1.4 1.4M13.2 13.2l1.4 1.4M3.4 14.6l1.4-1.4M13.2 4.8l1.4-1.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          )}
        </button>

        {costToday !== null && (
          <span className="topnav-cost topnav-cost-static" title="Estimated API spend today">
            ${costToday.toFixed(2)}
          </span>
        )}
        {/* Any model run from this browser, on every tab (2026-10-22). */}
        <RunBadge />
        <RunToasts />

        {/* A toothed cog, not a circle with rays. The previous icon was a circle
            with eight radiating lines — the same shape as the light-mode sun on
            the theme toggle beside it, which is why the two were confused. */}
        <button className={`topnav-icon-btn ${activePage === 'settings' ? 'active' : ''}`} onClick={() => setPage(activePage === 'settings' ? null : 'settings')} title="Settings">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path
              d="M19.4 13a7.7 7.7 0 000-2l2-1.6-2-3.4-2.4 1a7.6 7.6 0 00-1.7-1l-.4-2.6h-4l-.4 2.6a7.6 7.6 0 00-1.7 1l-2.4-1-2 3.4L6.6 11a7.7 7.7 0 000 2l-2 1.6 2 3.4 2.4-1a7.6 7.6 0 001.7 1l.4 2.6h4l.4-2.6a7.6 7.6 0 001.7-1l2.4 1 2-3.4-2-1.6z"
              stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"
            />
            <circle cx="12" cy="12" r="2.6" stroke="currentColor" strokeWidth="1.6"/>
          </svg>
        </button>

        <button className="topnav-icon-btn topnav-logout" onClick={logout} title="Sign out">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M12 6l3 3-3 3M7 9h8M7 3H4.5A1.5 1.5 0 003 4.5v9A1.5 1.5 0 004.5 15H7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
    </nav>
  );
}
